import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SearchResults from '../components/SearchResults';
import FilterBar, { yearKeyFor } from '../components/FilterBar';
import ExploreHome from '../components/ExploreHome';
import MovieModal from '../components/MovieModal';
import ManhuaResults from '../components/ManhuaResults';
import ManhuaHome from '../components/ManhuaHome';
import TrailerModal from '../components/TrailerModal';
import AuthModal from '../components/AuthModal';
import Footer from '../components/Footer';
import RegionSelector from '../components/RegionSelector';
import RagExperimentResults from '../components/RagExperimentResults';
import { useRegion } from '../contexts/RegionContext';
import {
  searchMovies,
  searchManhua,
  checkHealth,
  getStats,
  getManhuaStats,
  getStreamers,
  searchRagExperiment,
} from '../services/api';
import { supabase, isAuthConfigured } from '../services/supabase';
import { initAnalytics, trackEvent, identifyUser } from '../services/analytics';
import { hasIndustryAccess } from '../services/industryAccess';
import { hasRagExperimentAccess } from '../services/ragExperimentAccess';

// Suggested searches (italic serif links under the hero).
const MOVIE_SUGGESTIONS = [
  'sci-fi movies about time travel',
  'romantic comedies with strong female leads',
  'dark psychological thrillers from the ’90s',
  'movies like Inception, but more action-packed',
];
const MANHUA_SUGGESTIONS = [
  'regression revenge thriller with a calculating lead',
  'cute slice of life with cooking and romance',
  'fantasy academy with an overpowered hidden talent',
  'reincarnated as a villainess, but smarter this time',
];

const SEARCH_LIMIT = 40;
const DEFAULT_FILTERS = { providers: [], yearKey: 'any', contentType: null, yearStart: null, yearEnd: null };

// ── small editorial primitives ───────────────────────────────────────────────
const SERIF = "'Newsreader', Georgia, serif";
const Micro = ({ children, style }) => (
  <span style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#A39C8F', ...style }}>{children}</span>
);

function HomePage({ initialMode = 'movies' }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { region } = useRegion();
  const [mode, setMode] = useState(initialMode);
  // Tracks the query we've already mirrored to / restored from the URL, so the
  // restore effect doesn't re-fire for a search we just ran ourselves.
  const lastUrlQuery = useRef(null);

  // Movie/series state
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentQuery, setCurrentQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [playingTrailerKey, setPlayingTrailerKey] = useState(null);
  const [streamerBrands, setStreamerBrands] = useState([]);
  const [movieFilters, setMovieFilters] = useState(DEFAULT_FILTERS);
  const [experimentArm, setExperimentArm] = useState('standard');
  const [experimentResult, setExperimentResult] = useState(null);

  // Auth (Supabase)
  const [user, setUser] = useState(null);
  const [authModal, setAuthModal] = useState(null);

  // Manhua state
  const [manhuaResults, setManhuaResults] = useState(null);
  const [manhuaLoading, setManhuaLoading] = useState(false);
  const [manhuaError, setManhuaError] = useState(null);
  const [manhuaQuery, setManhuaQuery] = useState('');
  const [manhuaStats, setManhuaStats] = useState(null);

  // Shared search input (hero + results nav) and the "thinking" ticker.
  const [searchInput, setSearchInput] = useState('');
  const [thinkStep, setThinkStep] = useState(0);

  useEffect(() => {
    initAnalytics();
    trackEvent('App Loaded');
    (async () => {
      try {
        const health = await checkHealth();
        if (health.qdrant_connected) {
          const [dbStats, mhStats] = await Promise.all([
            getStats().catch(() => null),
            getManhuaStats().catch(() => null),
          ]);
          setStats(dbStats);
          setManhuaStats(mhStats);
        }
      } catch { /* offline — hero still renders */ }
    })();
    getStreamers().then((d) => setStreamerBrands(d.brands || []));
  }, []);

  useEffect(() => {
    if (!isAuthConfigured) return;
    supabase.auth.getSession().then(({ data }) => setUser(data.session?.user ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) { identifyUser(u.id); trackEvent('Auth Succeeded', { method: u.app_metadata?.provider || 'email' }); }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const signOut = async () => { if (supabase) await supabase.auth.signOut(); trackEvent('Logged Out'); };

  const handleMovieSearch = async (query) => {
    setIsLoading(true); setError(null); setCurrentQuery(query); setSearchInput(query);
    setMovieFilters(DEFAULT_FILTERS);
    // Mirror the query into the URL so it survives navigating to a detail view
    // and back (set the ref first so the restore effect ignores our own write).
    lastUrlQuery.current = query;
    setSearchParams({ q: query, ...(experimentArm !== 'standard' ? { rag: experimentArm } : {}) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    trackEvent('Search Performed', { query, length: query.length, region, rag_architecture: experimentArm });
    try {
      if (experimentArm !== 'standard') {
        setSearchResults(null);
        const result = await searchRagExperiment(query, experimentArm);
        setExperimentResult(result);
        trackEvent('RAG Experiment Completed', {
          architecture: experimentArm, recommendation_count: result?.recommendations?.length || 0,
          latency_ms: result?.latency_ms, provider: result?.provider,
        });
        return;
      }
      setExperimentResult(null);
      const results = await searchMovies(query, SEARCH_LIMIT, null, region);
      setSearchResults(results);
      const resultCount = results?.results?.length || 0;
      trackEvent('Search Completed', { query, result_count: resultCount, zero_results: resultCount === 0 });
      const af = results.applied_filters;
      if (af) {
        setMovieFilters({
          providers: af.brands || [],
          yearKey: yearKeyFor(af.year_start, af.year_end),
          yearStart: af.year_start ?? null,
          yearEnd: af.year_end ?? null,
          contentType: af.content_type ?? null,
        });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || err.message || 'Search failed.'); setSearchResults(null); setExperimentResult(null);
    } finally { setIsLoading(false); }
  };

  const applyMovieFilters = async (next) => {
    setMovieFilters(next);
    if (!currentQuery) return;
    setIsLoading(true); setError(null);
    try {
      const results = await searchMovies(currentQuery, SEARCH_LIMIT, {
        providers: next.providers, yearStart: next.yearStart, yearEnd: next.yearEnd, contentType: next.contentType,
      }, region);
      setSearchResults(results);
    } catch (err) { setError(err.message || 'Search failed.'); }
    finally { setIsLoading(false); }
  };

  // Region change re-ranks results around streaming availability in the chosen
  // country (and refreshes the "Available on" badges). Re-run the active movie
  // search, preserving any explicit filters. No-op on the manhua tab / empty query.
  useEffect(() => {
    if (mode !== 'movies' || !currentQuery || experimentArm !== 'standard') return;
    let alive = true;
    (async () => {
      setIsLoading(true); setError(null);
      const hasFilters = movieFilters.providers.length > 0 || movieFilters.yearKey !== 'any' || !!movieFilters.contentType;
      try {
        const results = await searchMovies(currentQuery, SEARCH_LIMIT,
          hasFilters
            ? { providers: movieFilters.providers, yearStart: movieFilters.yearStart, yearEnd: movieFilters.yearEnd, contentType: movieFilters.contentType }
            : null,
          region);
        if (alive) setSearchResults(results);
      } catch (err) {
        if (alive) setError(err.message || 'Search failed.');
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, experimentArm]);

  const handleManhuaSearch = async (query) => {
    setManhuaLoading(true); setManhuaError(null); setManhuaQuery(query); setSearchInput(query);
    // Mirror into the URL (?q=) so returning from a manhua detail page restores
    // these results instead of remounting to the empty hero.
    lastUrlQuery.current = query;
    setSearchParams({ q: query });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    trackEvent('Manhua Search Performed', { query, length: query.length });
    try {
      const results = await searchManhua(query, SEARCH_LIMIT);
      setManhuaResults(results);
      const resultCount = results?.results?.length || 0;
      trackEvent('Manhua Search Completed', { query, result_count: resultCount, zero_results: resultCount === 0 });
    } catch (err) { setManhuaError(err.message || 'Search failed.'); setManhuaResults(null); }
    finally { setManhuaLoading(false); }
  };

  const isManhua = mode === 'manhua';
  const handleSearch = isManhua ? handleManhuaSearch : handleMovieSearch;
  const activeIsLoading = isManhua ? manhuaLoading : isLoading;
  const activeQuery = isManhua ? manhuaQuery : currentQuery;
  const suggestions = isManhua ? MANHUA_SUGGESTIONS : MOVIE_SUGGESTIONS;

  // Include the error state so a failed/slow search shows an in-place message
  // instead of silently reverting to the hero (which looked like "loads forever
  // then goes back to the homepage").
  const hasSearched = isManhua
    ? manhuaResults !== null || manhuaLoading || !!manhuaError
    : searchResults !== null || experimentResult !== null || isLoading || !!error;

  // Thinking ticker — rotates while a request is in flight.
  useEffect(() => {
    if (!activeIsLoading) return;
    setThinkStep(0);
    const t = setInterval(() => setThinkStep((s) => s + 1), 750);
    return () => clearInterval(t);
  }, [activeIsLoading]);

  // Restore a search from the URL (?q=) — on first load, on a shared link, and
  // (the important case) when the user hits Back from a manhua detail page, which
  // remounts this component. Runs the search for the current mode unless we just
  // ran it ourselves (guarded by lastUrlQuery).
  useEffect(() => {
    const q = (searchParams.get('q') || '').trim();
    if (!q || q === lastUrlQuery.current) return;
    lastUrlQuery.current = q;
    setSearchInput(q);
    (isManhua ? handleManhuaSearch : handleMovieSearch)(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isManhua]);

  const goHome = () => {
    setSearchResults(null); setCurrentQuery(''); setError(null); setMovieFilters(DEFAULT_FILTERS);
    setExperimentResult(null);
    setManhuaResults(null); setManhuaQuery(''); setManhuaError(null); setSearchInput('');
    lastUrlQuery.current = null;
    setSearchParams({});
  };
  const switchMode = (next) => { setMode(next); setSearchInput(''); navigate(next === 'manhua' ? '/manhua' : '/'); };
  const handleManhuaClick = (manhua) => navigate(`/manhua/${manhua.id}`);

  const submitSearch = () => { const q = searchInput.trim(); if (q && !activeIsLoading) handleSearch(q); };
  const onKey = (e) => { if (e.key === 'Enter') submitSearch(); };

  const moviesN = stats?.total_movies;
  const manhuaN = manhuaStats?.total_manhua;
  const statLine = (moviesN || manhuaN)
    ? `${(moviesN || 0).toLocaleString()} films & series · ${(manhuaN || 0).toLocaleString()} manhua · indexed weekly`
    : 'AI-curated films, series & manhua · indexed weekly';

  const thinkLines = isManhua
    ? [`Reading ${(manhuaN || 20076).toLocaleString()} synopses…`, 'Matching tropes, tone, and pace — not just tags…', 'Choosing three worth your night…']
    : [`Reading ${(moviesN || 70000).toLocaleString()} loglines…`, 'Matching plot, tone, and pace — not just keywords…', 'Choosing three worth your evening…'];

  const showNewHint = hasSearched && searchInput.trim().length > 0 && searchInput.trim() !== activeQuery;

  const ragAccess = hasRagExperimentAccess(user);
  const chooseExperimentArm = (arm) => {
    setExperimentArm(arm);
    setExperimentResult(null); setSearchResults(null); setError(null);
    trackEvent('RAG Experiment Arm Selected', { architecture: arm });
  };
  const RagSelector = ({ compact = false }) => !isManhua && ragAccess ? (
    <label className="flex items-center gap-2" style={{ fontSize: compact ? 11 : 12, color: '#A39C8F' }}>
      <span className={compact ? 'hidden lg:inline' : ''}>Search method</span>
      <select value={experimentArm} onChange={(event) => chooseExperimentArm(event.target.value)}
        aria-label="RAG experiment search method"
        style={{ background: '#1d191d', color: '#F1ECE2', border: '1px solid rgba(220,168,94,0.45)', borderRadius: 6, padding: compact ? '5px 8px' : '8px 10px' }}>
        <option value="standard">Live search</option>
        <option value="llm">LLM only</option>
        <option value="vector">Vector RAG</option>
        <option value="hybrid">Hybrid RAG</option>
        <option value="graph">Graph RAG</option>
      </select>
    </label>
  ) : null;

  // ── segmented Movies / Manhua control ──────────────────────────────────────
  const Segmented = ({ small }) => (
    <div className="flex rounded-full overflow-hidden" style={{ border: '1px solid rgba(241,236,226,0.16)', fontSize: small ? 12 : 13 }}>
      <button onClick={() => switchMode('movies')} className="transition-colors"
        style={{ padding: small ? '6px 14px' : '7px 16px', whiteSpace: 'nowrap', fontWeight: 600, background: !isManhua ? '#F1ECE2' : 'transparent', color: !isManhua ? '#141114' : '#A39C8F' }}>
        Movies &amp; Series
      </button>
      <button onClick={() => switchMode('manhua')} className="transition-colors"
        style={{ padding: small ? '6px 14px' : '7px 16px', whiteSpace: 'nowrap', fontWeight: 600, background: isManhua ? '#F1ECE2' : 'transparent', color: isManhua ? '#141114' : '#A39C8F' }}>
        Manhua
      </button>
    </div>
  );

  const AuthControls = () =>
    user ? (
      <div className="flex items-center gap-3">
        {hasIndustryAccess(user) && <Link to="/industry" className="text-sm text-binge-gold hover:text-binge-ink transition-colors">Industry</Link>}
        <Link to="/profile" title="Profile & settings"
          className="hidden sm:inline text-sm text-binge-sub hover:text-binge-ink transition-colors max-w-[12rem] truncate">{user.email}</Link>
        <Link to="/profile" className="sm:hidden text-sm text-binge-sub hover:text-binge-ink transition-colors">Profile</Link>
        <button onClick={signOut} className="text-sm text-binge-sub hover:text-binge-ink transition-colors">Log out</button>
      </div>
    ) : (
      <div className="flex items-center gap-5">
        <button onClick={() => setAuthModal('login')} className="text-sm text-binge-sub hover:text-binge-ink transition-colors">Log in</button>
        <button onClick={() => setAuthModal('signup')}
          className="text-sm font-semibold rounded-full px-4 py-2 transition-colors"
          style={{ background: '#F1ECE2', color: '#141114' }}>
          Sign up
        </button>
      </div>
    );

  const Brand = ({ size = 24, onClick }) => (
    <button onClick={onClick} className="flex items-baseline gap-2.5" style={{ background: 'transparent', border: 'none', cursor: onClick ? 'pointer' : 'default', padding: 0 }}>
      <span style={{ fontFamily: SERIF, fontSize: size, fontWeight: 600, letterSpacing: '-0.01em', color: '#F1ECE2' }}>What to Binge</span>
      <span style={{ fontSize: 10, letterSpacing: '0.18em', color: '#DCA85E', border: '1px solid rgba(220,168,94,0.45)', borderRadius: 3, padding: '2px 5px', transform: 'translateY(-2px)' }}>AI</span>
    </button>
  );

  return (
    <div className="min-h-screen" style={{ background: '#141114', color: '#F1ECE2', fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>

      {/* ============================ HOME (pre-search) ============================ */}
      {!hasSearched && (
        <div className="animate-fadein">
          {/* Nav */}
          <div className="flex items-center justify-between px-6 sm:px-12 py-[22px]" style={{ borderBottom: '1px solid rgba(241,236,226,0.1)' }}>
            <Brand />
            <div className="hidden sm:flex items-center gap-5"><Segmented />{!isManhua && <RegionSelector />}<AuthControls /></div>
            <div className="sm:hidden flex items-center gap-3">{!isManhua && <RegionSelector compact />}<AuthControls /></div>
          </div>

          {/* Hero */}
          <div className="mx-auto px-6 text-center" style={{ maxWidth: 860, padding: '88px 24px 72px' }}>
            <div className="flex items-center justify-center gap-3.5">
              <span className="hidden sm:block" style={{ width: 48, height: 1, background: 'rgba(241,236,226,0.25)' }} />
              <Micro style={{ letterSpacing: '0.22em' }}>{statLine}</Micro>
              <span className="hidden sm:block" style={{ width: 48, height: 1, background: 'rgba(241,236,226,0.25)' }} />
            </div>

            <h1 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 'clamp(40px, 7vw, 76px)', lineHeight: 1.04, letterSpacing: '-0.015em', margin: '32px 0 0', textWrap: 'balance' }}>
              {isManhua ? (<>Describe the webtoon you <em style={{ fontStyle: 'italic', color: '#DCA85E' }}>can’t name</em>.</>)
                        : (<>Describe the film you <em style={{ fontStyle: 'italic', color: '#DCA85E' }}>can’t name</em>.</>)}
            </h1>

            <p style={{ fontSize: 18, lineHeight: 1.6, color: '#A39C8F', maxWidth: 520, margin: '24px auto 0' }}>
              {isManhua ? 'A trope, a mood, half a memory. Our curator has read everything, so you don’t have to scroll.'
                        : 'A plot, a mood, half a memory. Our curator has read everything, so you don’t have to scroll.'}
            </p>

            {/* Search field (underline serif) */}
            <div className="flex items-center gap-4 mx-auto" style={{ maxWidth: 680, margin: '52px auto 0', borderBottom: '2px solid rgba(241,236,226,0.55)', paddingBottom: 14 }}>
              <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={onKey} autoFocus
                className="binge-input" placeholder={isManhua ? 'a regression revenge story with a cold protagonist…' : 'a heist movie that feels like a hangover…'}
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontFamily: SERIF, fontSize: 'clamp(18px, 3.4vw, 26px)', color: '#F1ECE2', padding: 0 }} />
              <button onClick={submitSearch} className="hover:text-binge-ink transition-colors"
                style={{ cursor: 'pointer', background: 'transparent', border: 'none', fontSize: 13, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#DCA85E', whiteSpace: 'nowrap' }}>
                Search ↵
              </button>
            </div>
            {ragAccess && <div className="flex justify-center mt-5"><RagSelector /></div>}

            {/* Suggestions */}
            <div className="flex flex-wrap justify-center items-baseline gap-x-2.5 gap-y-2" style={{ marginTop: 34, fontSize: 15, color: '#A39C8F' }}>
              <Micro style={{ fontSize: 11, marginRight: 4 }}>Try</Micro>
              {suggestions.map((sug) => (
                <button key={sug} onClick={() => { setSearchInput(sug); handleSearch(sug); }}
                  className="hover:text-binge-gold transition-colors"
                  style={{ cursor: 'pointer', background: 'transparent', border: 'none', padding: 0, fontFamily: SERIF, fontStyle: 'italic', fontSize: 17, color: '#C9C2B4', borderBottom: '1px solid rgba(241,236,226,0.25)' }}>
                  {sug}
                </button>
              ))}
            </div>

            {/* Mobile segmented + region */}
            <div className="sm:hidden flex justify-center items-center gap-3 mt-10"><Segmented small />{!isManhua && <RegionSelector compact />}</div>
          </div>

          {/* Browse rails (movies/series) — immediate engagement, revealed on scroll */}
          {!isManhua && (
            <div className="mx-auto px-4 sm:px-10 pb-20" style={{ maxWidth: 1440 }}>
              <ExploreHome onItemClick={setSelectedMovie} onPlayTrailer={setPlayingTrailerKey} />
            </div>
          )}

          {/* Browse rails (manhua) — Popular, Latest Updates, genres, demographics.
              Only English-readable titles. Mirrors the movie ExploreHome. */}
          {isManhua && (
            <div className="mx-auto px-4 sm:px-10 pb-20" style={{ maxWidth: 1440 }}>
              <ManhuaHome onCardClick={handleManhuaClick} stats={manhuaStats} />
            </div>
          )}

          {/* Footer strip */}
          <div className="flex flex-wrap items-baseline justify-center gap-x-4 gap-y-2 px-6 sm:px-12 py-[22px]" style={{ borderTop: '1px solid rgba(241,236,226,0.1)' }}>
            <Micro style={{ fontSize: 10, letterSpacing: '0.2em', color: '#6E675D' }}>Searches across</Micro>
            <span style={{ fontSize: 13, color: '#A39C8F' }}>
              Netflix &nbsp;·&nbsp; Prime Video &nbsp;·&nbsp; Disney+ &nbsp;·&nbsp; Max &nbsp;·&nbsp; Apple TV+ &nbsp;·&nbsp; Hulu &nbsp;·&nbsp; Crunchyroll
            </span>
          </div>
        </div>
      )}

      {/* ============================ RESULTS ============================ */}
      {hasSearched && (
        <div className="animate-fadein">
          {/* Sticky nav with inline search */}
          <div className="sticky top-0 z-50"
            style={{ borderBottom: '1px solid rgba(241,236,226,0.1)', background: 'rgba(20,17,20,0.92)', backdropFilter: 'blur(12px)' }}>
            <div className="mx-auto flex items-center gap-5 sm:gap-8 px-4 sm:px-10 py-4" style={{ maxWidth: 1440 }}>
              <Brand size={20} onClick={goHome} />
              <div className="flex-1 relative min-w-0">
                <div className="flex items-center gap-3.5" style={{ borderBottom: '1px solid rgba(241,236,226,0.3)', paddingBottom: 6 }}>
                  <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={onKey} className="binge-input"
                    style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', fontFamily: SERIF, fontSize: 17, color: '#F1ECE2', padding: 0 }} />
                  <button onClick={submitSearch} className="hover:text-binge-ink transition-colors"
                    style={{ cursor: 'pointer', background: 'transparent', border: 'none', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#DCA85E', whiteSpace: 'nowrap' }}>Search</button>
                </div>
                {showNewHint && (
                  <div className="animate-fadein" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 7, fontSize: 11.5, color: '#DCA85E', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                    ↵ Enter runs a fresh search
                  </div>
                )}
              </div>
              <div className="hidden sm:block shrink-0"><RagSelector compact /></div>
              {!isManhua && <div className="hidden md:block shrink-0"><RegionSelector compact /></div>}
              <div className="hidden md:block shrink-0"><Segmented small /></div>
            </div>
          </div>

          <div className="mx-auto px-4 sm:px-10 pb-24" style={{ maxWidth: 1440 }}>
            {/* Hard filters (movies only) */}
            {!isManhua && experimentArm === 'standard' && searchResults && !isLoading && (
              <FilterBar
                brands={streamerBrands}
                providers={movieFilters.providers}
                yearKey={movieFilters.yearKey}
                contentType={movieFilters.contentType}
                onChange={applyMovieFilters}
              />
            )}

            {/* Thinking state */}
            {activeIsLoading ? (
              <div className="mx-auto text-center" style={{ maxWidth: 720, padding: '80px 24px' }}>
                <Micro style={{ fontSize: 11, letterSpacing: '0.2em', color: '#6E675D' }}>Reading the index for</Micro>
                <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 'clamp(24px, 5vw, 34px)', color: '#F1ECE2', marginTop: 14, textWrap: 'balance' }}>
                  “{activeQuery}”
                </div>
                <div className="flex justify-center gap-3.5" style={{ margin: '40px 0 28px' }}>
                  {[0, 0.2, 0.4].map((d) => (
                    <div key={d} className="animate-poster-pulse" style={{ width: 84, aspectRatio: '2/3', borderRadius: 4, background: 'rgba(241,236,226,0.07)', animationDelay: `${d}s` }} />
                  ))}
                </div>
                <div className="animate-blink" style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 16, color: '#A39C8F' }}>
                  {thinkLines[thinkStep % thinkLines.length]}
                </div>
              </div>
            ) : isManhua ? (
              <ManhuaResults
                searchResults={manhuaResults}
                isLoading={manhuaLoading}
                error={manhuaError}
                currentQuery={manhuaQuery}
                onCardClick={handleManhuaClick}
                stats={manhuaStats}
              />
            ) : experimentArm !== 'standard' ? (
              <RagExperimentResults result={experimentResult} error={error} query={currentQuery} />
            ) : (
              <SearchResults
                searchResults={searchResults}
                isLoading={isLoading}
                error={error}
                onMovieClick={setSelectedMovie}
                currentQuery={currentQuery}
                onPlayTrailer={setPlayingTrailerKey}
              />
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer attribution={isManhua ? 'Catalog from MangaDex · ratings via AniList' : 'Film & series data from The Movie Database (TMDB)'} />

      {selectedMovie && <MovieModal movie={selectedMovie} onClose={() => setSelectedMovie(null)} />}
      {playingTrailerKey && <TrailerModal trailerKey={playingTrailerKey} onClose={() => setPlayingTrailerKey(null)} />}
      {authModal && <AuthModal mode={authModal} onClose={() => setAuthModal(null)} />}
    </div>
  );
}

export default HomePage;
